import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './SkillsTerminal.css';

const groups = {
  programmation: {
    label: 'Programmation',
    color: '#7ee787',
    items: ['Python', 'Java', 'C# · C++', 'JavaScript · TypeScript', 'Node.js', 'PHP', 'Rust', 'HTML · CSS', 'Matlab'],
  },
  data: {
    label: 'Data',
    color: '#79c0ff',
    items: ['SQL', 'MySQL', 'PostgreSQL', 'MongoDB', 'NoSQL', 'Firebase'],
  },
  frameworks: {
    label: 'Frameworks',
    color: '#ff7b72',
    items: ['React · Next.js', 'React Native', 'Angular', 'Vue.js', 'Symfony', 'Flask', 'JavaFX', 'Spring Boot', 'Bootstrap'],
  },
  ia: {
    label: 'IA / ML',
    color: '#d2a8ff',
    items: ['TensorFlow', 'PyTorch'],
  },
  devops: {
    label: 'DevOps & Outils',
    color: '#ffa657',
    items: ['Docker', 'CI/CD', 'JBoss', 'WebSphere', 'Azure DevOps', 'Git', 'Postman', 'PowerApps · PowerAutomate', 'Figma', 'WordPress'],
  },
  env: {
    label: 'Environnements',
    color: '#a5d6ff',
    items: ['Ubuntu · Debian', 'Kali · Fedora', 'Windows · macOS', 'VSCode', 'IntelliJ', 'Eclipse'],
  },
  methodologie: {
    label: 'Méthodologie',
    color: '#f0883e',
    items: ['Agile · Scrum'],
  },
};

const ALIASES = {
  '/lang': '/programmation',
  '/langs': '/programmation',
  '/code': '/programmation',
  '/db': '/data',
  '/database': '/data',
  '/fw': '/frameworks',
  '/framework': '/frameworks',
  '/ml': '/ia',
  '/ai': '/ia',
  '/ops': '/devops',
  '/os': '/env',
  '/ide': '/env',
  '/agile': '/methodologie',
  '/scrum': '/methodologie',
  '/methodo': '/methodologie',
  'clear': '/clear',
  'cls': '/clear',
  'reset': '/clear',
  'help': '/help',
};

const COMMANDS = [
  { cmd: '/all', desc: 'toutes les compétences', color: '#7ee787' },
  { cmd: '/programmation', desc: 'langages', color: '#7ee787' },
  { cmd: '/data', desc: 'BDD & data', color: '#79c0ff' },
  { cmd: '/frameworks', desc: 'stack web & mobile', color: '#ff7b72' },
  { cmd: '/ia', desc: 'IA / Machine Learning', color: '#d2a8ff' },
  { cmd: '/devops', desc: 'DevOps & outils', color: '#ffa657' },
  { cmd: '/env', desc: 'OS & IDE', color: '#a5d6ff' },
  { cmd: '/methodologie', desc: 'Agile · Scrum', color: '#f0883e' },
  { cmd: '/whoami', desc: 'à propos d\'Eren', color: '#79c0ff' },
  { cmd: '/help', desc: 'liste des commandes', color: '#79c0ff' },
  { cmd: '/clear', desc: 'efface le terminal', color: '#8b949e' },
];

function resolveCmd(raw) {
  const cmd = raw.trim().toLowerCase();
  if (!cmd) return { type: 'noop' };
  const c = ALIASES[cmd] || cmd;
  if (c === '/help') return { type: 'help' };
  if (c === '/clear') return { type: 'clear' };
  if (c === '/all') return { type: 'all' };
  if (c === '/whoami') return { type: 'whoami' };
  const key = c.startsWith('/') ? c.slice(1) : c;
  if (groups[key]) return { type: 'group', key };
  return { type: 'error', text: raw };
}

function buildLines(out) {
  if (out.type === 'help') {
    const lines = [{ kind: 'muted', text: 'Available commands:' }];
    COMMANDS.forEach(({ cmd, color, desc }) => {
      lines.push({ kind: 'help', cmd, color, desc });
    });
    lines.push({ kind: 'muted', text: 'Astuce : tape « / » puis ↑/↓ pour naviguer.' });
    return lines;
  }
  if (out.type === 'all') {
    const lines = [];
    Object.entries(groups).forEach(([, g]) => {
      lines.push({ kind: 'group', label: g.label, color: g.color });
      g.items.forEach(it => lines.push({ kind: 'item', text: it, color: g.color }));
    });
    lines.push({ kind: 'muted', text: `\n→ ${Object.values(groups).reduce((n, g) => n + g.items.length, 0)} compétences listées.` });
    return lines;
  }
  if (out.type === 'group') {
    const g = groups[out.key];
    const lines = [{ kind: 'group', label: g.label, color: g.color }];
    g.items.forEach(it => lines.push({ kind: 'item', text: it, color: g.color }));
    return lines;
  }
  if (out.type === 'whoami') {
    return [
      { kind: 'muted', text: 'Eren Varli' },
      { kind: 'muted', text: 'Étudiant spécialisé en Intelligence Artificielle.' },
      { kind: 'muted', text: 'Développeur web & mobile, data scientist en alternance chez Volvo Group.' },
    ];
  }
  if (out.type === 'error') {
    return [{ kind: 'error', text: `command not found: ${out.text} (tape /help)` }];
  }
  return [];
}

function Prompt() {
  return (
    <span className="term__prompt">
      <span className="term__user">eren</span>
      <span className="term__muted">@</span>
      <span className="term__host">portfolio</span>
      <span className="term__muted">:</span>
      <span className="term__path">~</span>
      <span className="term__muted">$</span>
    </span>
  );
}

function OutputLine({ line, delay }) {
  const style = { animationDelay: `${delay}s` };
  if (line.kind === 'group') {
    return <p className="term__line term__group" style={{ ...style, color: line.color }}>[{line.label}]</p>;
  }
  if (line.kind === 'item') {
    return (
      <p className="term__line term__item" style={style}>
        <span style={{ color: line.color }}>›</span>{' '}{line.text}
      </p>
    );
  }
  if (line.kind === 'help') {
    return (
      <p className="term__line term__item" style={style}>
        <span style={{ color: line.color }}>{line.cmd}</span>
        <span className="term__muted">  ·  {line.desc}</span>
      </p>
    );
  }
  if (line.kind === 'error') {
    return <p className="term__line term__error" style={style}>{line.text}</p>;
  }
  return <p className="term__line term__muted" style={style}>{line.text}</p>;
}

export default function SkillsTerminal() {
  const [history, setHistory] = useState([]);
  const [input, setInput] = useState('');
  const [cmdHistory, setCmdHistory] = useState([]);
  const [histIndex, setHistIndex] = useState(-1);
  const [suggestIndex, setSuggestIndex] = useState(0);
  const bodyRef = useRef(null);
  const inputRef = useRef(null);

  const suggestions = useMemo(() => {
    if (!input.startsWith('/')) return [];
    const lower = input.toLowerCase();
    return COMMANDS.filter(c => c.cmd.startsWith(lower));
  }, [input]);

  useEffect(() => {
    setSuggestIndex(0);
  }, [input]);

  const exec = useCallback((rawCmd) => {
    const out = resolveCmd(rawCmd);
    if (out.type === 'noop') return;
    if (out.type === 'clear') {
      setHistory([]);
      return;
    }
    const lines = buildLines(out);
    setHistory(h => [...h, { id: `${Date.now()}-${Math.random()}`, cmd: rawCmd, lines }]);
    setCmdHistory(h => [rawCmd, ...h].slice(0, 50));
    setHistIndex(-1);
  }, []);

  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [history, suggestions.length]);

  const runAndClear = (cmd) => {
    exec(cmd);
    setInput('');
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (suggestions.length > 0) {
      runAndClear(suggestions[suggestIndex].cmd);
      return;
    }
    if (input.trim()) runAndClear(input.trim());
  };

  const onKeyDown = (e) => {
    if (suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSuggestIndex(i => (i + 1) % suggestions.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSuggestIndex(i => (i - 1 + suggestions.length) % suggestions.length);
        return;
      }
      if (e.key === 'Tab') {
        e.preventDefault();
        setInput(suggestions[suggestIndex].cmd);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setInput('');
        return;
      }
    } else {
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        const next = Math.min(histIndex + 1, cmdHistory.length - 1);
        if (cmdHistory[next] !== undefined) {
          setInput(cmdHistory[next]);
          setHistIndex(next);
        }
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        const next = histIndex - 1;
        if (next < 0) {
          setInput('');
          setHistIndex(-1);
        } else {
          setInput(cmdHistory[next]);
          setHistIndex(next);
        }
      }
    }
  };

  return (
    <div className="terminal">
      <div className="terminal__head">
        <span className="terminal__dot terminal__dot--r" />
        <span className="terminal__dot terminal__dot--y" />
        <span className="terminal__dot terminal__dot--g" />
        <span className="terminal__title">eren@portfolio · skills.sh</span>
      </div>

      <div
        className="terminal__body"
        ref={bodyRef}
        onClick={(e) => {
          if (e.target === bodyRef.current) inputRef.current?.focus();
        }}
      >
        <p className="term__line term__muted term__welcome">
          <span style={{ color: '#7ee787' }}>›</span>{' '}
          Bienvenue. Tape <span className="term__kbd">/</span> pour afficher les commandes
          disponibles, navigue avec <span className="term__kbd">↑</span>{' '}
          <span className="term__kbd">↓</span> et valide avec <span className="term__kbd">↵</span>.
        </p>

        {history.map(entry => (
          <div key={entry.id} className="term__entry">
            <p className="term__line">
              <Prompt />
              <span className="term__cmd">{' '}{entry.cmd}</span>
            </p>
            <div className="term__output">
              {entry.lines.map((line, i) => (
                <OutputLine key={i} line={line} delay={i * 0.022} />
              ))}
            </div>
          </div>
        ))}

        <div className="term__prompt-wrap">
          <form className="term__line term__active" onSubmit={onSubmit}>
            <Prompt />
            <input
              ref={inputRef}
              className="term__input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              spellCheck={false}
              autoCapitalize="off"
              autoComplete="off"
              autoCorrect="off"
              placeholder="tape « / » pour commencer"
              role="combobox"
              aria-label="Terminal input"
              aria-autocomplete="list"
              aria-controls="term-suggest"
              aria-expanded={suggestions.length > 0}
              aria-activedescendant={
                suggestions.length > 0 ? `term-suggest-${suggestIndex}` : undefined
              }
            />
            <span className="term__cursor" aria-hidden="true" />
          </form>
          {suggestions.length > 0 && (
            <ul
              className="term__suggest"
              id="term-suggest"
              role="listbox"
              aria-label="Commandes"
            >
              {suggestions.map((s, i) => (
                <li
                  key={s.cmd}
                  id={`term-suggest-${i}`}
                  role="option"
                  aria-selected={i === suggestIndex}
                  className={`term__suggest-item${i === suggestIndex ? ' is-selected' : ''}`}
                  onMouseEnter={() => setSuggestIndex(i)}
                  onMouseDown={(e) => { e.preventDefault(); runAndClear(s.cmd); }}
                >
                  <span className="term__suggest-cmd" style={{ color: s.color }}>{s.cmd}</span>
                  <span className="term__suggest-desc">{s.desc}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
